import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, openAuthedFile, errText } from '@/lib/api';
import { EmployerFormScan } from '@/components/EmployerFormScan';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/StatusBadge';
import { ProcedureSelect } from '@/components/ProcedureSelect';
import { CodePicker } from '@/components/CodePicker';
import { FunctionalCapacityMatrix } from '@/components/FunctionalCapacityMatrix';
import { Send, Upload, Trash2, ArrowLeft, AlertCircle, HelpCircle, Sparkles, Ban, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { useFaxStatus } from '@/lib/serverMeta';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';

// Why a case is being closed without a decision. Codes match the backend's
// CANCEL_REASON_LABELS in routers/nurse.py; the server is the source of truth.
const CANCEL_REASONS = [
  ['duplicate', 'Duplicate of another case'],
  ['created_in_error', 'Created in error'],
  ['patient_withdrew', 'The patient no longer needs it'],
  ['other', 'Another reason (explain below)'],
];

function Section({ title, children }) {
  return <Card><CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader><CardContent>{children}</CardContent></Card>;
}

// Friendly labels for patient "I don't know — ask my office" gap fields
const UNKNOWN_FIELD_LABELS = {
  employer_contact: 'Employer HR contact (email/phone)',
  leave_end: 'Expected return / leave end date',
};

const SECTION_TAB_LABELS = {
  fmla: 'FMLA',
  std: 'STD',
  ltd: 'LTD',
  ada: 'ADA',
  life_ins: 'Life Insurance',
  sick_pool: 'Sick-Leave Pool',
  hipaa_release: 'HIPAA Release',
};

const REVISION_CATEGORY_LABELS = {
  my_details: 'Name / personal details',
  dates: 'Dates',
  employer: 'Employer information',
  other: 'Other',
};

function RevisionPanel({ sub, sid, onChanged }) {
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);
  const openRevs = (sub.revision_requests || []).filter((r) => r.status === 'open');
  const handledCount = (sub.revision_requests || []).length - openRevs.length;
  if (openRevs.length === 0) return null;
  const awaitingDoctor = sub.status === 'awaiting_doctor';

  const resolve = async (rid, action, reopen) => {
    setBusyId(rid);
    try {
      const r = await api.post(`/submissions/${sid}/revision-requests/${rid}/resolve`, {
        action, note: notes[rid] || '', reopen,
      });
      toast.success(action === 'resolved'
        ? (r.data.reopened ? 'Marked fixed — pulled back from the doctor for edits' : 'Marked fixed')
        : 'Request dismissed');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the request');
    } finally { setBusyId(null); }
  };

  return (
    <div className="rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] p-3 mb-4" data-testid="nurse-revision-panel">
      <div className="flex items-start gap-2 text-[hsl(32_92%_28%)]">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="font-medium">The patient requested {openRevs.length === 1 ? 'a change' : `${openRevs.length} changes`}{handledCount > 0 ? ` (${handledCount} earlier handled)` : ''}:</div>
      </div>
      <ul className="mt-2 space-y-3">
        {openRevs.map((r) => (
          <li key={r.id} className="rounded-md border border-[hsl(45_93%_84%)] bg-card p-3" data-testid={`nurse-revision-item-${r.id}`}>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{REVISION_CATEGORY_LABELS[r.category] || 'Change request'}</div>
            <p className="text-sm mt-1">{r.message}</p>
            <Input
              className="mt-2 h-8 text-sm"
              placeholder="Optional note back to the patient (e.g. 'Fixed — start date updated')"
              value={notes[r.id] || ''}
              onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
              data-testid={`nurse-revision-note-${r.id}`}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" disabled={busyId === r.id} onClick={() => resolve(r.id, 'resolved', awaitingDoctor)} data-testid={`nurse-revision-resolve-${r.id}`}>
                {awaitingDoctor ? 'Mark fixed & pull back from doctor' : 'Mark fixed'}
              </Button>
              <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => resolve(r.id, 'dismissed', false)} data-testid={`nurse-revision-dismiss-${r.id}`}>
                Dismiss
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Why a direct-to-doctor case was held for staff (ADR 0009) — the completeness
// gate's check names, which `direct_handoff` stores on the case.
const HELD_GAP_LABELS = {
  diagnosis_code_present: 'No diagnosis code',
  employer_form_answered: "Unanswered questions on the employer's form",
  employer_contact_present: 'No employer contact to deliver to',
  handoff_error: 'The automatic hand-off failed',
};

export default function NurseDetail() {
  const faxStatus = useFaxStatus();
  const { sid } = useParams();
  const navigate = useNavigate();
  const { packetTypes } = useApp();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('duplicate');
  const [cancelNote, setCancelNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get(`/submissions/${sid}`);
    setData(r.data);
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  // §2 (2026-09-06): the Save button was redundant — send-to-doctor saved anyway —
  // so edits now save themselves, a second after the last keystroke. `dirtyRef`
  // is what separates a nurse's edit from a reload, and `dataRef` lets the
  // unmount flush read the latest draft rather than a stale closure.
  const dataRef = useRef(null);
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  dataRef.current = data;

  const flush = useCallback(async () => {
    clearTimeout(timerRef.current);
    const sub = dataRef.current?.submission;
    if (!sub || !dirtyRef.current) return;
    dirtyRef.current = false;
    setSaveState('saving');
    try {
      await api.patch(`/submissions/${sid}/clinical`, {
        nurse_clinical: sub.nurse_clinical,
        functional_capacity: sub.functional_capacity,
        std: sub.std,
        ltd: sub.ltd,
        ada: sub.ada,
        life_ins: sub.life_ins,
        sick_pool: sub.sick_pool,
        hipaa_release: sub.hipaa_release,
        delivery: sub.delivery,
        nurse_note_to_doctor: sub.nurse_note_to_doctor,
        packet_type_id: sub.packet_type_id,
      });
      setSaveState('saved');
    } catch (e) {
      dirtyRef.current = true; // keep it dirty so the next edit retries
      setSaveState('error');
      toast.error(errText(e, 'Could not save your changes'));
      throw e;
    }
  }, [sid]);

  useEffect(() => {
    if (!dirtyRef.current) return undefined;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flush().catch(() => {}); }, 1000);
    return () => clearTimeout(timerRef.current);
  }, [data, flush]);
  // Leaving the page with a pending edit: save it now rather than lose it.
  useEffect(() => () => { if (dirtyRef.current) flush().catch(() => {}); }, [flush]);
  // Who can sign. A failure here must not block the handoff — the picker just
  // stays empty and the case goes to the shared pool.
  useEffect(() => {
    api.get('/practice/doctors').then((r) => setDoctors(r.data || [])).catch(() => {});
  }, []);

  if (!data) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const sub = data.submission;
  const pt = packetTypes.find(p => p.id === sub.packet_type_id) || data.packet_type || {};
  const sections = pt.sections || ['fmla'];
  // 'declined' is terminal: the provider decided, and editing the clinical detail
  // afterwards would change the record a decision was made against (LB-LIFE-4).
  // 'cancelled' too: the case is over, and the only thing left to read is why.
  const locked = ['awaiting_doctor', 'signed', 'complete', 'amended', 'declined', 'cancelled'].includes(sub.status);
  // The patient has not submitted yet: there is nothing for a doctor to decide.
  const cannotSend = locked || sub.status === 'draft_patient' || !sub.nurse_clinical?.icd10;
  // A case that has reached a doctor was billed on arrival, so the server refuses
  // to cancel it (review F2) — the doctor declines it instead.
  const cancellable = !locked && !sub.presented_at;
  // After submission the link is the patient's status page (download, HR report).
  const renewable = !['draft_patient', 'cancelled', 'declined', 'abandoned'].includes(sub.status);

  const renewLink = async () => {
    try {
      const r = await api.post(`/submissions/${sid}/magic-link/resend`);
      const link = `${window.location.origin}${r.data.magic_link_path}`;
      try { await navigator.clipboard.writeText(link); } catch { /* shown below instead */ }
      toast.success('Patient link renewed for 90 days and copied', { description: 'Send it to the patient yourself — nothing was emailed.' });
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const cancelCase = async () => {
    setSending(true);
    try {
      await api.post(`/submissions/${sid}/cancel`, { reason_code: cancelReason, note: cancelNote });
      toast.success('Case cancelled');
      setCancelOpen(false);
      navigate('/nurse');
    } catch (e) { toast.error(errText(e)); } finally { setSending(false); }
  };

  const set = (key, value) => { dirtyRef.current = true; setData(d => ({ ...d, submission: { ...d.submission, [key]: value } })); };
  // IMPORTANT: functional updates that read from the LATEST draft — several of
  // these fire in the same tick (e.g. procedure prefill), and spreading the
  // render-time `sub` would make each call overwrite the previous one.
  const setField = (section) => (k, v) => { dirtyRef.current = true; setData(d => ({
    ...d,
    submission: { ...d.submission, [section]: { ...(d.submission[section] || {}), [k]: v } },
  })); };
  const setClinical = setField('nurse_clinical');
  const setSTD = setField('std');
  const setLTD = setField('ltd');
  const setADA = setField('ada');
  const setLife = setField('life_ins');
  const setSickPool = setField('sick_pool');
  const setHipaa = setField('hipaa_release');
  const setDelivery = setField('delivery');
  const setFC = (v) => set('functional_capacity', v);

  const sendToDoctor = async () => {
    setSending(true);
    try {
      await flush(); // anything still in the debounce window goes with it
      // Empty doctorId means "leave unclaimed" — the backend auto-assigns when
      // the practice has exactly one doctor, so a solo office never sees this.
      await api.post(`/submissions/${sid}/send-to-doctor`, { note, doctor_id: doctorId });
      toast.success('Sent to doctor');
      navigate('/nurse');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/submissions/${sid}/uploads`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (e) { toast.error('Upload failed'); }
  };

  const removeUpload = async (uid) => {
    await api.delete(`/submissions/${sid}/uploads/${uid}`);
    toast.success('Removed');
    load();
  };

  const onProcedure = (id, name) => {
    if (!id) {
      // Cleared back to manual entry — keep any hand-typed ICD/diagnosis
      setClinical('procedure_id', '');
      setClinical('procedure_name', '');
      return;
    }
    api.get('/catalog').then(r => {
      const found = (r.data || []).find(x => x.id === id);
      // Apply EVERYTHING in one atomic update so nothing overwrites anything
      setData(d => {
        const s = d.submission;
        const sev = s.nurse_clinical?.severity || 'Moderate';
        const variant = (found?.severity_variants || {})[sev] || {};
        const restrictions = variant.restrictions || {};
        const clinical = {
          ...(s.nurse_clinical || {}),
          procedure_id: id,
          procedure_name: name,
        };
        if (found?.icd10) clinical.icd10 = found.icd10;
        if (found) clinical.cpt = found.cpt || '';
        if (found?.name && !s.nurse_clinical?.diagnosis_description) clinical.diagnosis_description = found.name;
        if (found?.incapacity_type) clinical.incapacity_type = found.incapacity_type;
        const cur = s.functional_capacity?.current || {};
        const fc = {
          ...(s.functional_capacity || {}),
          current: {
            ...cur,
            lifting_lbs: restrictions.lifting_lbs ?? cur.lifting_lbs,
            standing: restrictions.standing_hours ?? cur.standing,
          },
        };
        return { ...d, submission: { ...s, nurse_clinical: clinical, functional_capacity: fc } };
      });
      if (found) {
        const sev = sub.nurse_clinical?.severity || 'Moderate';
        toast.message(`Pre-filled from "${found.name}" template (${sev})`, {
          description: found.icd10 ? `ICD-10 ${found.icd10} applied — review before sending.` : undefined,
        });
      }
    }).catch(() => {
      setClinical('procedure_id', id);
      setClinical('procedure_name', name);
    });
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/nurse')} data-testid="nurse-detail-back"><ArrowLeft className="h-4 w-4 mr-1" /> Queue</Button>
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">{sub.patient_form?.full_name || 'Submission'}</h1>
        <StatusBadge status={sub.status} overdue={sub._overdue} />
        {sub.patient_form?.preferred_language === 'es' && (
          <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30" data-testid="nurse-detail-lang-badge">
            Prefers Spanish · Prefiere español
          </Badge>
        )}
        <div className="flex-1" />
        {cancellable && (
          <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="nurse-cancel-case-button"><Ban className="h-4 w-4 mr-1" /> Cancel case</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Cancel this case</DialogTitle>
                <DialogDescription>
                  It leaves the queue and the patient's link stops working. The practice is not billed for it. This cannot be undone.
                  {sub.payment?.method === 'stripe_checkout' && ' This patient already paid by card — refund them from the Stripe dashboard.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Reason</Label>
                  <Select value={cancelReason} onValueChange={setCancelReason}>
                    <SelectTrigger data-testid="nurse-cancel-reason-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CANCEL_REASONS.map(([code, label]) => <SelectItem key={code} value={code}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea placeholder={cancelReason === 'other' ? 'Why? (required)' : 'Optional note for the next person who opens this case'} value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} data-testid="nurse-cancel-note" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep case</Button>
                <Button variant="destructive" onClick={cancelCase} disabled={sending || (cancelReason === 'other' && !cancelNote.trim())} data-testid="nurse-cancel-confirm">Cancel case</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {!locked && saveState !== 'idle' && (
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="nurse-autosave-state">
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : 'Not saved'}
          </span>
        )}
        {/* §2 (2026-09-06): a one-doctor practice has nobody to pick, so the
            confirmation dialog was a tap for nothing — Send sends. The note is
            still there, behind one tap, for the nurse who has something to say. */}
        {doctors.length <= 1 && !locked && (
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setNoteOpen((o) => !o)} data-testid="nurse-note-toggle">
            <StickyNote className="h-4 w-4 mr-1" /> Note to doctor
          </Button>
        )}
        {doctors.length <= 1 ? (
          <Button disabled={cannotSend || sending} onClick={sendToDoctor} data-testid="nurse-send-to-doctor-button">
            <Send className="h-4 w-4 mr-1" /> {sending ? 'Sending…' : 'Send to doctor'}
          </Button>
        ) : (
        <Dialog>
          <DialogTrigger asChild>
            <Button disabled={cannotSend} data-testid="nurse-send-to-doctor-button"><Send className="h-4 w-4 mr-1" /> Send to doctor</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Send to doctor</DialogTitle><DialogDescription>Lock nurse editing and notify the doctor that this submission is ready for review.</DialogDescription></DialogHeader>
            {doctors.length > 1 && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Assign to</Label>
                <Select value={doctorId || 'unassigned'} onValueChange={(v) => setDoctorId(v === 'unassigned' ? '' : v)}>
                  <SelectTrigger data-testid="nurse-assign-doctor-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Any available doctor</SelectItem>
                    {doctors.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Only the assigned doctor sees this case and gets the notification. "Any available doctor"
                  leaves it visible to all of them.
                </p>
              </div>
            )}
            <Textarea placeholder="Optional note to doctor" value={note} onChange={(e) => setNote(e.target.value)} data-testid="nurse-note-to-doctor" />
            <DialogFooter><Button onClick={sendToDoctor} disabled={sending} data-testid="nurse-send-confirm">Send</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>
      {doctors.length <= 1 && !locked && noteOpen && (
        <Textarea
          className="mb-4"
          placeholder="Optional note to the doctor — goes with the case when you send it"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="nurse-note-to-doctor"
        />
      )}

      {sub.cancellation && (
        <div className="rounded-lg border bg-muted p-3 mb-4 flex items-start gap-2" data-testid="nurse-cancelled-banner">
          <Ban className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-medium">{sub.cancellation.by} cancelled this case — {(CANCEL_REASONS.find(([c]) => c === sub.cancellation.reason_code) || [])[1] || (sub.cancellation.reason_code || '').replace(/_/g, ' ')}</div>
            {sub.cancellation.note && <div className="text-sm mt-0.5">{sub.cancellation.note}</div>}
          </div>
        </div>
      )}

      {/* The provider's decision not to certify, and what to tell the patient.
          This case will not move again — the phone call is the only next step. */}
      {sub.decline && (
        <div className="rounded-lg border bg-muted p-3 mb-4 flex items-start gap-2" data-testid="nurse-decline-banner">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-medium">{sub.decline.by} did not certify this case — please contact the patient.</div>
            <div className="text-sm">{(sub.decline.reason_code || '').replace(/_/g, ' ')}</div>
            {sub.decline.note && <div className="text-sm mt-0.5">{sub.decline.note}</div>}
          </div>
        </div>
      )}

      {sub.doctor_note_to_nurse && sub.status === 'returned' && (
        <div className="rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] p-3 mb-4 flex items-start gap-2" data-testid="nurse-doctor-return-note">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div><div className="font-medium">Doctor returned this with a comment:</div><div>{sub.doctor_note_to_nurse}</div></div>
        </div>
      )}

      {/* ADR 0009: a direct-to-doctor practice holds back a case the completeness gate refuses. */}
      {sub.direct_handoff?.status === 'held' && ['new', 'in_review'].includes(sub.status) && (
        <div className="rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] p-3 mb-4 flex items-start gap-2" data-testid="nurse-direct-held">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-medium">This case could not go straight to the doctor.</div>
            <div>{(sub.direct_handoff.gaps || []).map((g) => HELD_GAP_LABELS[g] || 'Something needs checking').join(' · ')} — fix it here, then send it to the doctor.</div>
          </div>
        </div>
      )}

      <RevisionPanel sub={sub} sid={sid} onChanged={load} />

      {sub.auto_prep && sub.auto_prep.status === 'done' && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 mb-4 flex items-start gap-2" data-testid="nurse-auto-prep-banner">
          <Sparkles className="h-4 w-4 mt-0.5 text-primary" />
          <div className="text-sm">
            <div className="font-medium">AI pre-filled this case{sub.auto_prep.rationale ? ` — ${sub.auto_prep.rationale}` : ''}</div>
            {(sub.auto_prep.flags || []).length > 0 ? (
              <ul className="list-disc ml-4 mt-0.5 text-[hsl(32_92%_28%)]">
                {(sub.auto_prep.flags || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            ) : (
              <div className="text-muted-foreground">No flags — verify and send to the doctor.</div>
            )}
          </div>
        </div>
      )}

      {(sub.patient_form?.unknown_fields || []).length > 0 && (
        <div className="rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] p-3 mb-4 flex items-start gap-2" data-testid="nurse-gaps-banner">
          <HelpCircle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-medium">Patient marked these as "I don't know — ask my office":</div>
            <ul className="list-disc ml-4 mt-0.5">
              {(sub.patient_form.unknown_fields || []).map((f) => (
                <li key={f} data-testid={`nurse-gap-item-${f}`}>{UNKNOWN_FIELD_LABELS[f] || f}</li>
              ))}
            </ul>
            <div className="text-xs mt-1">Fill these in below before sending to the doctor (HR contact under Delivery; return date via the clinical estimate).</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Packet Type">
            <Select value={sub.packet_type_id} onValueChange={(v) => set('packet_type_id', v)} disabled={locked}>
              <SelectTrigger data-testid="nurse-packet-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{packetTypes.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </Section>

          <Section title="Patient information (read-only)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {[['Full name', sub.patient_form?.full_name], ['DOB', sub.patient_form?.dob], ['Employer', sub.patient_form?.employer_name], ['Job title', sub.patient_form?.job_title], ['Reason', sub.patient_form?.reason_for_leave], ['Leave', `${sub.patient_form?.leave_start || ''} → ${sub.patient_form?.leave_end || ''}`]].map(([k, v]) => (
                <div key={k}><div className="text-xs text-muted-foreground">{k}</div><div>{v || '—'}</div></div>
              ))}
            </div>
          </Section>

          <Section title="Clinical Information (Nurse)">
            <div className="space-y-3">
              <ProcedureSelect
                value={sub.nurse_clinical?.procedure_id}
                onChange={onProcedure}
                severity={sub.nurse_clinical?.severity}
                onSeverityChange={(v) => setClinical('severity', v)}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>ICD-10 code</Label>
                  <CodePicker kind="icd10" value={sub.nurse_clinical?.icd10 || ''} onChange={(v) => setClinical('icd10', v)}
                    onPick={(c) => { if (!sub.nurse_clinical?.diagnosis_description) setClinical('diagnosis_description', c.description); }}
                    disabled={locked} placeholder="Code or words, e.g. M75.102 or rotator cuff" testid="nurse-icd10" />
                  <p className="text-[11px] text-muted-foreground mt-1">Auto-fills when you pick a procedure from the catalog above — search here for uncommon diagnoses.</p>
                </div>
                <div>
                  <Label>CPT code</Label>
                  <CodePicker kind="procedure" value={sub.nurse_clinical?.cpt || ''} onChange={(v) => setClinical('cpt', v)} disabled={locked} placeholder="e.g. 29881" testid="nurse-cpt" />
                </div>
                <div><Label>Procedure date</Label><Input type="date" value={sub.nurse_clinical?.procedure_date || ''} onChange={(e) => setClinical('procedure_date', e.target.value)} disabled={locked} data-testid="nurse-procedure-date" /></div>
                <div className="md:col-span-2"><Label>Diagnosis description</Label><Input value={sub.nurse_clinical?.diagnosis_description || ''} onChange={(e) => setClinical('diagnosis_description', e.target.value)} disabled={locked} data-testid="nurse-diagnosis" /></div>
                <div><Label>Incapacity type</Label>
                  <Select value={sub.nurse_clinical?.incapacity_type || 'Continuous'} onValueChange={(v) => setClinical('incapacity_type', v)} disabled={locked}>
                    <SelectTrigger data-testid="nurse-incapacity-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Continuous">Continuous</SelectItem>
                      <SelectItem value="Reduced Schedule">Reduced Schedule</SelectItem>
                      <SelectItem value="Episodic">Episodic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2"><Label>Clinical notes</Label><Textarea rows={3} value={sub.nurse_clinical?.clinical_notes || ''} onChange={(e) => setClinical('clinical_notes', e.target.value)} disabled={locked} data-testid="nurse-clinical-notes" /></div>
              </div>
            </div>
          </Section>

          {(pt.fc_required || sections.includes('std') || sections.includes('ltd') || sections.includes('ada')) && (
            <Section title="Functional Capacity">
              <FunctionalCapacityMatrix value={sub.functional_capacity} onChange={setFC} readOnly={locked} />
            </Section>
          )}

          <Section title="Section details">
            <Tabs defaultValue={sections[0] || 'fmla'}>
              <TabsList>
                {sections.map(s => <TabsTrigger key={s} value={s} data-testid={`nurse-section-tab-${s}`}>{s.toUpperCase()}</TabsTrigger>)}
              </TabsList>
              <TabsContent value="fmla" className="mt-3 text-sm text-muted-foreground">FMLA fields are derived from patient + clinical sections above. Provider signature finalizes this packet.</TabsContent>
              <TabsContent value="std" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>STD insurer</Label><Input value={sub.std?.insurer || ''} onChange={(e) => setSTD('insurer', e.target.value)} disabled={locked} data-testid="std-insurer" /></div>
                <div><Label>Insurer contact</Label><Input value={sub.std?.insurer_contact || ''} onChange={(e) => setSTD('insurer_contact', e.target.value)} disabled={locked} data-testid="std-insurer-contact" /></div>
                <div><Label>Claim number</Label><Input value={sub.std?.claim_number || ''} onChange={(e) => setSTD('claim_number', e.target.value)} disabled={locked} data-testid="std-claim-number" /></div>
                <div><Label>Start date</Label><Input type="date" value={sub.std?.start_date || ''} onChange={(e) => setSTD('start_date', e.target.value)} disabled={locked} data-testid="std-start-date" /></div>
                <div><Label>Elimination period (days)</Label><Input type="number" value={sub.std?.elimination_period_days ?? 7} onChange={(e) => setSTD('elimination_period_days', parseInt(e.target.value || '0'))} disabled={locked} data-testid="std-elimination" /></div>
              </TabsContent>
              <TabsContent value="ltd" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>LTD insurer</Label><Input value={sub.ltd?.insurer || ''} onChange={(e) => setLTD('insurer', e.target.value)} disabled={locked} data-testid="ltd-insurer" /></div>
                <div><Label>Claim number</Label><Input value={sub.ltd?.claim_number || ''} onChange={(e) => setLTD('claim_number', e.target.value)} disabled={locked} data-testid="ltd-claim-number" /></div>
                <div><Label>Policy number</Label><Input value={sub.ltd?.policy_number || ''} onChange={(e) => setLTD('policy_number', e.target.value)} disabled={locked} data-testid="ltd-policy-number" /></div>
                <div><Label>Elimination period (days)</Label><Input type="number" value={sub.ltd?.elimination_period_days ?? 90} onChange={(e) => setLTD('elimination_period_days', parseInt(e.target.value || '0'))} disabled={locked} data-testid="ltd-elimination" /></div>
                <div className="md:col-span-2"><Label>Definition</Label>
                  <Select value={sub.ltd?.occupation_definition || 'Own occupation'} onValueChange={(v) => setLTD('occupation_definition', v)} disabled={locked}>
                    <SelectTrigger data-testid="ltd-definition"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Own occupation">Own occupation</SelectItem>
                      <SelectItem value="Any occupation">Any occupation</SelectItem>
                      <SelectItem value="Own occupation for 24 months, then any occupation">Own occupation for 24 months, then any occupation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
              <TabsContent value="ada" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label>Accommodation request</Label><Textarea rows={3} value={sub.ada?.request_description || ''} onChange={(e) => setADA('request_description', e.target.value)} disabled={locked} data-testid="ada-request" /></div>
                <div className="md:col-span-2"><Label>Functional limitations</Label><Textarea rows={3} value={sub.ada?.limitations || ''} onChange={(e) => setADA('limitations', e.target.value)} disabled={locked} data-testid="ada-limitations" /></div>
                <div className="md:col-span-2">
                  <Label>Recommended accommodations</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Remote work', 'Modified schedule', 'Assistive equipment', 'Reduced duties', 'Sit/stand workstation'].map(opt => {
                      const arr = sub.ada?.recommendations || [];
                      const on = arr.includes(opt);
                      return (
                        <Button key={opt} type="button" size="sm" variant={on ? 'default' : 'outline'} disabled={locked} onClick={() => setADA('recommendations', on ? arr.filter(x => x !== opt) : [...arr, opt])} data-testid={`ada-rec-${opt}`}>
                          {opt}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                <div><Label>Expected duration</Label><Input value={sub.ada?.expected_duration || ''} onChange={(e) => setADA('expected_duration', e.target.value)} disabled={locked} placeholder="e.g. 8 weeks" data-testid="ada-duration" /></div>
              </TabsContent>
              <TabsContent value="life_ins" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Insurer</Label><Input value={sub.life_ins?.insurer || ''} onChange={(e) => setLife('insurer', e.target.value)} disabled={locked} data-testid="life-insurer" /></div>
                <div><Label>Policy number</Label><Input value={sub.life_ins?.policy_number || ''} onChange={(e) => setLife('policy_number', e.target.value)} disabled={locked} data-testid="life-policy-number" /></div>
                <div><Label>Date of diagnosis</Label><Input type="date" value={sub.life_ins?.diagnosis_date || ''} onChange={(e) => setLife('diagnosis_date', e.target.value)} disabled={locked} data-testid="life-diagnosis-date" /></div>
                <div><Label>Date first treated</Label><Input type="date" value={sub.life_ins?.first_treated_date || ''} onChange={(e) => setLife('first_treated_date', e.target.value)} disabled={locked} data-testid="life-first-treated" /></div>
                <div className="md:col-span-2"><Label>Prognosis</Label><Textarea rows={2} value={sub.life_ins?.prognosis || ''} onChange={(e) => setLife('prognosis', e.target.value)} disabled={locked} data-testid="life-prognosis" /></div>
                <div className="md:col-span-2"><Label>Work capability statement</Label><Textarea rows={2} value={sub.life_ins?.work_capability || ''} onChange={(e) => setLife('work_capability', e.target.value)} disabled={locked} data-testid="life-work-capability" /></div>
              </TabsContent>
              <TabsContent value="sick_pool" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Pool / bank administrator</Label><Input value={sub.sick_pool?.administrator || ''} onChange={(e) => setSickPool('administrator', e.target.value)} disabled={locked} data-testid="sickpool-administrator" /></div>
                <div><Label>Employer / agency</Label><Input value={sub.sick_pool?.employer_agency || ''} onChange={(e) => setSickPool('employer_agency', e.target.value)} disabled={locked} data-testid="sickpool-employer" /></div>
                <div className="md:col-span-2"><Label>Severe condition certification</Label>
                  <Select value={sub.sick_pool?.severe_condition || ''} onValueChange={(v) => setSickPool('severe_condition', v)} disabled={locked}>
                    <SelectTrigger data-testid="sickpool-severe"><SelectValue placeholder="Select certification" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes — condition meets pool criteria">Yes — condition meets pool criteria</SelectItem>
                      <SelectItem value="No — does not meet criteria">No — does not meet criteria</SelectItem>
                      <SelectItem value="Pending further evaluation">Pending further evaluation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Expected duration of absence</Label><Input value={sub.sick_pool?.expected_duration || ''} onChange={(e) => setSickPool('expected_duration', e.target.value)} disabled={locked} placeholder="e.g. 6 weeks" data-testid="sickpool-duration" /></div>
                <div><Label>Eligible return date</Label><Input type="date" value={sub.sick_pool?.eligible_return_date || ''} onChange={(e) => setSickPool('eligible_return_date', e.target.value)} disabled={locked} data-testid="sickpool-return-date" /></div>
              </TabsContent>
              <TabsContent value="hipaa_release" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Authorized recipient</Label><Input value={sub.hipaa_release?.recipient || ''} onChange={(e) => setHipaa('recipient', e.target.value)} disabled={locked} placeholder="e.g. Acme Corp HR / insurer" data-testid="hipaa-recipient" /></div>
                <div><Label>Authorization expires</Label><Input type="date" value={sub.hipaa_release?.expiration_date || ''} onChange={(e) => setHipaa('expiration_date', e.target.value)} disabled={locked} data-testid="hipaa-expiration" /></div>
                <div className="md:col-span-2"><Label>Information to release</Label><Textarea rows={2} value={sub.hipaa_release?.scope || ''} onChange={(e) => setHipaa('scope', e.target.value)} disabled={locked} placeholder="e.g. Records related to knee surgery, 2024–2025" data-testid="hipaa-scope" /></div>
                <div className="md:col-span-2"><Label>Purpose</Label><Input value={sub.hipaa_release?.purpose || ''} onChange={(e) => setHipaa('purpose', e.target.value)} disabled={locked} placeholder="e.g. Leave administration" data-testid="hipaa-purpose" /></div>
              </TabsContent>
            </Tabs>
          </Section>
        </div>

        <div className="space-y-4">
          <Section title="Delivery & due date">
            <div className="space-y-3">
              <div><Label>Due date (employer)</Label><Input type="date" value={sub.delivery?.due_date || ''} onChange={(e) => setDelivery('due_date', e.target.value)} disabled={locked} data-testid="delivery-due-date" /></div>
              <div><Label>Method</Label>
                <Select value={sub.delivery?.method || 'Email'} onValueChange={(v) => setDelivery('method', v)} disabled={locked}>
                  <SelectTrigger data-testid="delivery-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Fax" disabled={!faxStatus.enabled}>
                      {faxStatus.enabled ? 'Fax' : 'Fax (not available)'}
                    </SelectItem>
                    <SelectItem value="Patient-collected">Patient-collected</SelectItem>
                  </SelectContent>
                </Select>
                {/* GAPS R6: fax used to be offered with no provider wired, and the
                    delivery log then claimed 'fax_sent'. Say so instead. */}
                {!faxStatus.enabled && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="delivery-fax-unavailable">
                    {faxStatus.reason}. Use Email or Patient-collected.
                  </p>
                )}
              </div>
              <div><Label>Employer email</Label><Input value={sub.delivery?.employer_email || ''} onChange={(e) => setDelivery('employer_email', e.target.value)} disabled={locked} data-testid="delivery-employer-email" /></div>
              <div><Label>Employer fax</Label><Input value={sub.delivery?.employer_fax || ''} onChange={(e) => setDelivery('employer_fax', e.target.value)} disabled={locked} data-testid="delivery-employer-fax" /></div>
            </div>
          </Section>

          <Section title="Employer form (HR) scan">
            <EmployerFormScan mode="staff" sid={sid} employerForm={sub.employer_form} locked={locked} onChanged={load} />
          </Section>

          <Section title="Paper records / uploads">
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="nurse-upload-input" />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={locked} data-testid="nurse-upload-button"><Upload className="h-4 w-4 mr-1" /> Upload (camera/file)</Button>
              <ul className="space-y-1 text-sm" data-testid="nurse-upload-list">
                {(sub.uploads || []).map(u => (
                  <li key={u.id} className="flex items-center justify-between rounded border bg-card px-2 py-1">
                    <button type="button" className="truncate text-left hover:underline" onClick={() => openAuthedFile(`/uploads/${sid}/${u.id}`)} data-testid={`nurse-upload-open-${u.id}`}>{u.name}</button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeUpload(u.id)} disabled={locked} data-testid={`nurse-upload-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </li>
                ))}
                {(sub.uploads || []).length === 0 && <li className="text-xs text-muted-foreground">No uploads yet.</li>}
              </ul>
            </div>
          </Section>

          <Section title="Magic link">
            <div className="text-xs text-muted-foreground break-all">{`${window.location.origin}/p/${sub.magic_token}`}</div>
            <div className="text-xs text-muted-foreground mt-1">Expires {String(sub.magic_token_expires).slice(0, 10)}</div>
            {renewable && (
              <Button variant="outline" size="sm" className="mt-2" onClick={renewLink} data-testid="nurse-renew-link-button">Renew &amp; copy link</Button>
            )}
          </Section>

          {sub.payment && (
            <Section title="Payment">
              <div className="text-sm">${sub.payment.amount?.toFixed(2)} · {sub.payment.method} {sub.payment.last4 ? `•••• ${sub.payment.last4}` : ''}</div>
              <div className="text-xs text-muted-foreground">Paid {String(sub.payment.paid_at).slice(0, 16).replace('T', ' ')}</div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
